import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Footer from '@/components/Footer';
import axios from 'axios';
import { 
  Building2, Users, MessageSquare, Search, ArrowRight, Briefcase,
  Scale, Calculator, Shield, TrendingUp, Target, Heart, Lightbulb,
  HardHat, Stethoscope, ChevronRight, Star, CheckCircle, Play,
  Monitor, UserCog, FileText, Home, PenTool, ShieldCheck, Gavel,
  ChevronLeft, MapPin, Clock, Flame
} from 'lucide-react';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_expert-connect-184/artifacts/zgfr8qi0_Consultant.svg";

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Animated counters
  const [counts, setCounts] = useState({ professionals: 0, companies: 0, categories: 0, satisfaction: 0 });
  
  // Recent listings for showcase
  const [recentListings, setRecentListings] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  
  // Fetch recent listings
  useEffect(() => {
    const fetchRecentListings = async () => {
      try {
        const response = await axios.get(`${API}/api/listings/public/recent?limit=12`);
        setRecentListings(response.data);
      } catch (error) {
        console.error('Error fetching recent listings:', error);
      }
    };
    fetchRecentListings();
  }, []);
  
  // Auto-scroll carousel
  useEffect(() => {
    if (recentListings.length === 0 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentSlide(prev => {
        const maxSlide = Math.max(0, recentListings.length - 4);
        return prev >= maxSlide ? 0 : prev + 1;
      });
    }, 4000);
    
    return () => clearInterval(interval);
  }, [recentListings.length, isPaused]);
  
  // Time ago helper
  const getTimeAgo = useCallback((dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffDays > 0) return `${diffDays} ${diffDays === 1 ? 'giorno' : 'giorni'} fa`;
    if (diffHours > 0) return `${diffHours} ${diffHours === 1 ? 'ora' : 'ore'} fa`;
    return 'Poco fa';
  }, []);
  
  useEffect(() => {
    const targets = { professionals: 500, companies: 200, categories: 15, satisfaction: 98 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setCounts({
        professionals: Math.round(targets.professionals * progress),
        companies: Math.round(targets.companies * progress),
        categories: Math.round(targets.categories * progress),
        satisfaction: Math.round(targets.satisfaction * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  // All available categories
  const allCategories = [
    { name: 'Avvocati', icon: <Scale className="w-8 h-8" /> },
    { name: 'Commercialisti', icon: <Calculator className="w-8 h-8" /> },
    { name: 'Consulenti Aziendali', icon: <Briefcase className="w-8 h-8" /> },
    { name: 'Ingegneri', icon: <HardHat className="w-8 h-8" /> },
    { name: 'Consulenti Marketing', icon: <Target className="w-8 h-8" /> },
    { name: 'Consulenti Finanziari', icon: <TrendingUp className="w-8 h-8" /> },
    { name: 'Medici del Lavoro', icon: <Stethoscope className="w-8 h-8" /> },
    { name: 'Consulenti HR', icon: <UserCog className="w-8 h-8" /> },
    { name: 'Consulenti IT', icon: <Monitor className="w-8 h-8" /> },
    { name: 'Consulenti Legali Aziendali', icon: <Gavel className="w-8 h-8" /> },
    { name: 'Consulenti Fiscali', icon: <FileText className="w-8 h-8" /> },
    { name: 'Architetti', icon: <Home className="w-8 h-8" /> },
    { name: 'Notai', icon: <PenTool className="w-8 h-8" /> },
    { name: 'Consulenti del Lavoro', icon: <Users className="w-8 h-8" /> },
    { name: 'Esperti Sicurezza sul Lavoro', icon: <ShieldCheck className="w-8 h-8" /> },
  ];

  // Random category selection based on current month (changes monthly)
  const [displayedCategories, setDisplayedCategories] = useState([]);
  
  useEffect(() => {
    // Use current month as seed for consistent monthly rotation
    const now = new Date();
    const monthSeed = now.getFullYear() * 12 + now.getMonth();
    
    // Shuffle categories using seeded random
    const shuffled = [...allCategories].sort((a, b) => {
      const hashA = (monthSeed * 31 + a.name.charCodeAt(0)) % 1000;
      const hashB = (monthSeed * 31 + b.name.charCodeAt(0)) % 1000;
      return hashA - hashB;
    });
    
    // Take first 8 categories and add random counts
    const selected = shuffled.slice(0, 8).map(cat => ({
      ...cat,
      count: Math.floor(Math.random() * 50) + 15 // Random count between 15-65
    }));
    
    setDisplayedCategories(selected);
  }, []);

  const testimonials = [
    {
      quote: "ConsultantHUB ci ha permesso di trovare il commercialista perfetto per la gestione della nostra contabilità in meno di una settimana!",
      author: "Giuseppe Nola",
      role: "Direttore Amministrativo, Nolatrans S.r.l.",
      rating: 5
    },
    {
      quote: "Piattaforma eccellente. Abbiamo trovato consulenti IT qualificati per la nostra migrazione cloud.",
      author: "Andrea Basile",
      role: "CEO, Evolution S.p.A.",
      rating: 5
    },
    {
      quote: "Come azienda in crescita, avevamo bisogno di un consulente del lavoro affidabile. ConsultantHUB ha superato le aspettative.",
      author: "Lucia Termini",
      role: "HR Manager, Termocostruzioni S.r.l.",
      rating: 5
    },
    {
      quote: "Servizio professionale e veloce. Abbiamo trovato l'avvocato giusto per le nostre esigenze contrattuali.",
      author: "Marco Delfino",
      role: "Titolare, Cleanstar S.r.l.",
      rating: 5
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/auth`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar - Transparent on hero */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center" data-testid="logo">
            <img src={LOGO_URL} alt="ConsultantHUB" className="h-14 w-auto" />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => navigate('/faq')} className="text-slate-600 hover:text-[#344B71] font-medium transition-colors">
              Come Funziona
            </button>
            <button onClick={() => navigate('/faq')} className="text-slate-600 hover:text-[#344B71] font-medium transition-colors">
              FAQ
            </button>
            <button onClick={() => navigate('/contatti')} className="text-slate-600 hover:text-[#344B71] font-medium transition-colors">
              Contatti
            </button>
          </div>
          <div className="flex items-center space-x-4">
            <Button 
              onClick={() => navigate('/auth')} 
              variant="ghost"
              className="text-[#344B71] hover:text-[#344B71] font-medium"
            >
              Accedi
            </Button>
            <Button 
              onClick={() => navigate('/auth')} 
              className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-full px-6 py-2 font-medium transition-all shadow-lg hover:shadow-xl"
              data-testid="login-button"
            >
              Registrati Gratis
            </Button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section - Gradient Background */}
      <section className="relative pt-24 pb-32 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#344B71] via-[#344B71] to-[#344B71]" />
        
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 pt-16">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold text-white tracking-tight leading-tight mb-6">
              Incontra il Professionista
              <span className="block text-[#E8A007] mt-2">Qualificato per la Tua Azienda</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Il marketplace B2B che connette aziende e consulenti qualificati. 
              Oltre 500 professionisti pronti ad aiutarti.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-12">
              <div className="flex flex-col sm:flex-row gap-3 bg-white p-2 rounded-2xl shadow-2xl">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Cerca per categoria (es. Commercialista, Avvocato...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 border-0 bg-transparent text-lg focus:ring-0"
                  />
                </div>
                <Button 
                  type="submit"
                  className="bg-[#E8A007] hover:bg-[#E09612] text-white rounded-xl px-8 h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  Cerca
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </form>
            
            {/* Stats */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {[
                { value: counts.professionals, suffix: '+', label: 'Professionisti' },
                { value: counts.companies, suffix: '+', label: 'Aziende' },
                { value: counts.categories, suffix: '', label: 'Categorie' },
                { value: counts.satisfaction, suffix: '%', label: 'Soddisfazione' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                    {stat.value}{stat.suffix}
                  </div>
                  <div className="text-white/70 text-sm">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
        
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="text-[#344B71] font-semibold text-sm uppercase tracking-wider">Categorie più richieste</span>
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mt-2 mb-4">
              Esplora per Categoria
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Le categorie professionali più ricercate negli ultimi 30 giorni
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {displayedCategories.map((category, index) => (
              <motion.div
                key={index}
                className="group bg-white border-2 border-slate-100 rounded-2xl p-6 text-center hover:border-[#344B71] hover:shadow-xl transition-all duration-300 cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate('/auth')}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-2xl flex items-center justify-center text-white mx-auto mb-4 group-hover:scale-110 transition-transform">
                  {category.icon}
                </div>
                <h3 className="font-semibold text-[#344B71] mb-1">{category.name}</h3>
                <p className="text-sm text-slate-500">{category.count} professionisti</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-[#344B71] text-[#344B71] hover:bg-[#344B71] hover:text-white rounded-full px-8"
            >
              Vedi tutte le categorie
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Recent Listings Showcase Section */}
      {recentListings.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <span className="text-[#E8A007] font-semibold text-sm uppercase tracking-wider">Professionisti Ricercati</span>
              <h2 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mt-2 mb-4">
                Figure Richieste Questa Settimana
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Scopri le ultime opportunità pubblicate dalle aziende
              </p>
            </motion.div>

            {/* Carousel Container */}
            <div 
              className="relative"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              ref={carouselRef}
            >
              {/* Navigation Arrows */}
              <button
                onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center text-[#344B71] hover:bg-[#344B71] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                disabled={currentSlide === 0}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <button
                onClick={() => setCurrentSlide(prev => Math.min(Math.max(0, recentListings.length - 4), prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center text-[#344B71] hover:bg-[#344B71] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                disabled={currentSlide >= recentListings.length - 4}
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Cards Container */}
              <div className="overflow-hidden mx-8">
                <motion.div 
                  className="flex gap-6"
                  animate={{ x: -currentSlide * (100 / 4 + 1.5) + '%' }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  {recentListings.map((listing, index) => (
                    <motion.div
                      key={listing.id || index}
                      className="flex-shrink-0 w-[calc(25%-18px)] min-w-[280px]"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div 
                        className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-slate-100 hover:border-[#344B71]/30 h-full flex flex-col group"
                        onClick={() => navigate('/auth')}
                      >
                        {/* Header with Priority Badge */}
                        <div className="flex items-start justify-between mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            listing.priority === 'alta' 
                              ? 'bg-red-100 text-red-700' 
                              : listing.priority === 'media'
                              ? 'bg-[#E8A007]/10 text-[#E8A007]'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {listing.priority === 'alta' && <Flame className="w-3 h-3 inline mr-1" />}
                            {listing.priority === 'alta' ? 'Urgente' : listing.priority === 'media' ? 'Media priorità' : 'Normale'}
                          </span>
                          <span className="text-xs text-slate-400 flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {getTimeAgo(listing.created_at)}
                          </span>
                        </div>
                        
                        {/* Category */}
                        <div className="mb-3">
                          <span className="text-sm font-semibold text-[#344B71] bg-[#344B71]/5 px-3 py-1 rounded-full">
                            {listing.category}
                          </span>
                        </div>
                        
                        {/* Title */}
                        <h3 className="font-bold text-[#344B71] text-lg mb-2 line-clamp-2 group-hover:text-[#E8A007] transition-colors">
                          {listing.title}
                        </h3>
                        
                        {/* Sector */}
                        {listing.sector && (
                          <p className="text-sm text-slate-500 mb-3 flex items-center">
                            <Briefcase className="w-4 h-4 mr-1.5 text-slate-400" />
                            {listing.sector}
                          </p>
                        )}
                        
                        {/* Location */}
                        <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-sm text-slate-600 flex items-center">
                            <MapPin className="w-4 h-4 mr-1.5 text-[#E8A007]" />
                            {listing.location_city || listing.location_province || (
                              listing.location_type === 'remote' ? 'Remoto' : 'Italia'
                            )}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            listing.location_type === 'remote' 
                              ? 'bg-emerald-100 text-emerald-700'
                              : listing.location_type === 'hybrid'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {listing.location_type === 'remote' ? 'Remote' : 
                             listing.location_type === 'hybrid' ? 'Ibrido' : 'In sede'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              {/* Dots Indicator */}
              <div className="flex justify-center mt-8 gap-2">
                {Array.from({ length: Math.max(1, recentListings.length - 3) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      currentSlide === index 
                        ? 'bg-[#344B71] w-8' 
                        : 'bg-slate-300 w-2 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-10">
              <Button
                onClick={() => navigate('/auth')}
                className="bg-[#344B71] hover:bg-[#2a3d5c] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Registrati per Candidarti
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="mt-4 text-sm text-slate-500">
                Registrazione gratuita • Inizia in meno di 2 minuti
              </p>
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="text-[#344B71] font-semibold text-sm uppercase tracking-wider">Processo</span>
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mt-2 mb-4">
              Come Funziona
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Quattro semplici passi per trovare il professionista ideale
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-24 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-[#344B71] via-[#E8A007] to-[#344B71] rounded-full" />
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  icon: <Users className="w-8 h-8" />,
                  title: 'Crea il Profilo',
                  description: 'Registrati gratuitamente e completa il tuo profilo professionale'
                },
                {
                  step: '02',
                  icon: <Search className="w-8 h-8" />,
                  title: 'Cerca e Filtra',
                  description: 'Trova professionisti per categoria, settore o località'
                },
                {
                  step: '03',
                  icon: <MessageSquare className="w-8 h-8" />,
                  title: 'Contatta',
                  description: 'Avvia una conversazione diretta con i professionisti'
                },
                {
                  step: '04',
                  icon: <CheckCircle className="w-8 h-8" />,
                  title: 'Collabora',
                  description: 'Inizia la collaborazione professionale'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="relative text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="relative z-10 w-20 h-20 bg-white border-4 border-[#344B71] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <div className="text-[#344B71]">{item.icon}</div>
                  </div>
                  <span className="inline-block bg-[#E8A007] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                    {item.step}
                  </span>
                  <h3 className="text-xl font-semibold text-[#344B71] mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-[#344B71] to-[#344B71]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="text-[#E8A007] font-semibold text-sm uppercase tracking-wider">Testimonianze</span>
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-white mt-2">
              Cosa Dicono i Nostri Utenti
            </h2>
          </motion.div>

          <motion.div
            key={currentTestimonial}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 text-center"
          >
            <div className="flex justify-center mb-6">
              {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-[#E8A007] fill-[#E8A007]" />
              ))}
            </div>
            <p className="text-xl md:text-2xl text-white font-light italic mb-8 leading-relaxed">
              &ldquo;{testimonials[currentTestimonial].quote}&rdquo;
            </p>
            <div className="flex items-center justify-center">
              <div className="w-14 h-14 bg-[#E8A007] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                {testimonials[currentTestimonial].author.charAt(0)}
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">{testimonials[currentTestimonial].author}</p>
                <p className="text-white/70 text-sm">{testimonials[currentTestimonial].role}</p>
              </div>
            </div>
          </motion.div>

          {/* Dots */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentTestimonial ? 'bg-[#E8A007] w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#344B71] font-semibold text-sm uppercase tracking-wider">Vantaggi</span>
              <h2 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mt-2 mb-6">
                Perché Scegliere ConsultantHUB
              </h2>
              <div className="space-y-6">
                {[
                  { title: 'Professionisti Verificati', desc: 'Tutti i professionisti vengono verificati prima di essere pubblicati' },
                  { title: 'Messaggistica Integrata', desc: 'Comunica direttamente sulla piattaforma senza condividere contatti' },
                  { title: 'Registrazione Gratuita', desc: 'Crea il tuo account gratuitamente e inizia a esplorare la piattaforma' },
                  { title: 'Ricerca Avanzata', desc: 'Filtra per categoria, settore, località e disponibilità' }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-10 h-10 bg-[#344B71]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#344B71]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#344B71] mb-1">{benefit.title}</h4>
                      <p className="text-slate-600 text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#344B71]/10 to-[#344B71]/10 rounded-3xl p-8">
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop"
                  alt="Team collaboration"
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-3xl p-12 text-center relative overflow-hidden"
          >
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-playfair font-bold text-white mb-4">
                Pronto a Far Crescere il Tuo Business?
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                Unisciti a oltre 500 professionisti e 200 aziende che già usano ConsultantHUB
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/auth?type=professional')}
                  className="bg-[#E8A007] hover:bg-[#E09612] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
                >
                  Sono un Professionista
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  onClick={() => navigate('/auth?type=company')}
                  className="bg-white hover:bg-slate-100 text-[#344B71] rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
                >
                  Sono un&apos;Azienda
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
